import { ReactNode, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  clearFiltersCreatedByCreator,
  resetFiltersCreatedByCreator,
} from '../../../../../components/Crud/filters/components/DynamicOptionsSelectorsCreator/hooks/useDynamicOptionsSelectorsCreator';
import useMultiDynamicOptionsSelector from '../../../../../components/Crud/filters/hooks/multiOptionsSelectorFilter/useMultiDynamicOptionsSelector/useMultiDynamicOptionsSelector';
import useMultiStaticOptionsSelector from '../../../../../components/Crud/filters/hooks/multiOptionsSelectorFilter/useMultiStaticOptionsSelector';
import useRadioFilter from '../../../../../components/Crud/filters/hooks/radioFilter/useRadioFilter';
import useTextFilter from '../../../../../components/Crud/filters/hooks/textFilter/useTextFilter';
import {
  FilterOptionV3,
  MultiDynamicOptionsCreatorFilter,
  PossibleFilters,
} from '../../../../../components/Crud/filters/sharedBetweenFilters/types';
import useWidth from '../../../../../components/Hooks/useWidth';
import SearchField from '../../../../../components/v2/ui/FilterBar/components/SearchField/SearchField';
import useDefaultFiltersToList from '../../../../../hooks/useDefaultFiltersToList';
import useIsFirstRender from '../../../../../hooks/useIsFirstRender';
import useSearchParams from '../../../../../hooks/useSearchParams';
import LangEnum from '../../../../../lang/LangEnum';
import { getFindClassificationTypesKey } from '../../../../../services/modules/classificationTypes/classificationTypes.keys';
import classificationTypesRepository from '../../../../../services/modules/classificationTypes/classificationTypes.repository';
import {
  getFindCategoriesFilterOptionsKey,
  getFindClassificationsFilterOptionsKey,
  getFindCollectionsFilterOptionsKey,
  getFindColorsFilterOptionsKey,
  getFindDeliveryWindowsFilterOptionsKey,
  getFindReferenceColorsFilterOptionsKey,
  getFindSizesFilterOptionsKey,
} from '../../../../../services/modules/filterOptions/filterOptions.keys';
import filterOptionsRepository from '../../../../../services/modules/filterOptions/repositories/filterOptions.repository';
import { FilterPreferenceRecognizedLists } from '../../../../../services/modules/userContextPreferences/userContextPreferences.types';
import useGeneralSettingsStore from '../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import useIsAdminSeller from '../../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import { CatalogProductsTestIds } from '../../../../../utils/testIds/CatalogProducts.testIds';
import { CLASSIFICATIONS_PREFIX } from '../../../../classifications/classification-filter.utils';
import { ClassificationTypeEntityEnum } from '../../../../classifications/classification.types';
import { UsedInOfflineMode } from '../../../../offline/types/UsedInOfflineMode';
import productAvailabilityOptions from '../../../../products/options/productAvailabilityOptions';
import { useTranslation } from '../../../../translation/TranslationContext';
import { useAbility } from '../../../../users/AbilityContext';
import { CATALOG_FILTERS_KEYS } from '../../catalogFilters.constants';
import useSellInPriceRangeFilter from '../../hooks/useSellInPriceRangeFilter';

interface UseCatalogProductsFiltersParams extends UsedInOfflineMode {}

export interface UseCatalogProductsFiltersResult {
  options: FilterOptionV3[];
  preferenceList: FilterPreferenceRecognizedLists;
  searchField: ReactNode;
  isSearchActive: boolean;
  onClearSearch: () => void;
}

const useCatalogProductsFilters = ({
  isOfflineMode,
}: UseCatalogProductsFiltersParams): UseCatalogProductsFiltersResult => {
  const { translate } = useTranslation();
  const { mobileWidth } = useWidth();
  const isAdminSeller = useIsAdminSeller();
  const { id: catalogId } = useParams<{ id: string }>();
  const { searchParams } = useSearchParams();
  const ability = useAbility();
  const isFirstRender = useIsFirstRender();
  const { generalSettings } = useGeneralSettingsStore();

  const filterPreferenceList = (catalogId ||
    FilterPreferenceRecognizedLists.CATALOG_PRODUCTS) as FilterPreferenceRecognizedLists;

  const { defaultFilters, defaultFiltersByPrefix, shouldIgnoreDefaultFilters } =
    useDefaultFiltersToList({
      list: filterPreferenceList,
      filtersKeys: Object.values(CATALOG_FILTERS_KEYS),
      filterKeysPrefix: [CLASSIFICATIONS_PREFIX],
    });

  const showSellInPriceFilter = generalSettings.showSellInPriceFilterInCatalogs;
  const canReadSellInPriceFilter = ability.can('read', 'catalog-sell-in-price-filter');

  const sellInPriceRangeFilter = useSellInPriceRangeFilter({
    catalogId: catalogId || '',
    shouldIgnoreDefaultFilters,
    isEnabled: canReadSellInPriceFilter && showSellInPriceFilter && !isOfflineMode,
  });

  const repository = filterOptionsRepository(isOfflineMode);
  const classificationTypeRepository = classificationTypesRepository(isOfflineMode);

  const searchTextFilter = useTextFilter({
    key: CATALOG_FILTERS_KEYS.codeOrName,
    defaultValue: defaultFilters?.[CATALOG_FILTERS_KEYS.codeOrName] as string,
    shouldIgnoreDefaultFilters,
    isAutoApply: mobileWidth,
  });

  const {
    handleResetFilter: handleResetProductColorsFilter,
    getSearchParamKeys: getSearchParamKeysOfProductColorsFilter,
    cleanFilter: cleanProductColorsFilter,
    ...productColorsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findColorsFilterOptions,
    queryKeyFn: getFindColorsFilterOptionsKey,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_FILTERS_KEYS.productColor,
    defaultValues: defaultFilters?.[CATALOG_FILTERS_KEYS.productColor],
    shouldIgnoreDefaultFilters,
    formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
    defaultQueryParam: {
      catalogId,
      isAdminSeller,
      onlyActiveProducts: true,
      basedOnList: 'catalogProducts',
    },
    order: {
      code: 'ASC',
    },
  });

  const {
    handleResetFilter: handleResetCategoryFilter,
    getSearchParamKeys: getSearchParamKeysOfProductCategoryFilter,
    cleanFilter: cleanProductCategoryFilter,
    ...productCategoryFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCategoriesFilterOptions,
    queryKeyFn: getFindCategoriesFilterOptionsKey,
    paramKeyToSearchMore: 'nameOrCode',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_FILTERS_KEYS.productCategory,
    defaultValues: defaultFilters?.[CATALOG_FILTERS_KEYS.productCategory],
    shouldIgnoreDefaultFilters,
    formatLabel: (rawData) => `${rawData.name}`,
    defaultQueryParam: {
      catalogId,
      isAdminSeller,
      onlyActiveProducts: true,
      basedOnList: 'catalogProducts',
    },
    order: {
      name: 'ASC',
    },
    aggregatingOptionParams: {
      aggregatingValueKey: 'groupId',
      aggregatingLabelKey: 'groupName',
    },
  });

  const {
    handleResetFilter: handleResetSizesFilter,
    getSearchParamKeys: getSearchParamKeysOfProductSizesFilter,
    cleanFilter: cleanProductSizesFilter,
    ...productSizesFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findSizesFilterOptions,
    queryKeyFn: getFindSizesFilterOptionsKey,
    paramKeyToSearchMore: 'name',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_FILTERS_KEYS.productSizes,
    defaultValues: defaultFilters?.[CATALOG_FILTERS_KEYS.productSizes],
    shouldIgnoreDefaultFilters,
    formatLabel: (rawData) => `${rawData.name}`,
    defaultQueryParam: {
      catalogId,
      isAdminSeller,
      onlyActiveProducts: true,
      basedOnList: 'catalogProducts',
    },
    order: {
      position: 'ASC',
    },
  });

  const {
    handleResetFilter: handleResetCollectionsFilter,
    getSearchParamKeys: getSearchParamKeysOfCollectionsFilter,
    cleanFilter: cleanCollectionsFilter,
    ...collectionsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findCollectionsFilterOptions,
    queryKeyFn: getFindCollectionsFilterOptionsKey,
    paramKeyToSearchMore: 'query',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_FILTERS_KEYS.collections,
    defaultValues: defaultFilters?.[CATALOG_FILTERS_KEYS.collections],
    shouldIgnoreDefaultFilters,
    formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
    defaultQueryParam: {
      catalogId,
      isAdminSeller,
      onlyActiveProducts: true,
      basedOnList: 'catalogProducts',
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    handleResetFilter: handleResetDeliveryWindowFilter,
    getSearchParamKeys: getSearchParamKeysOfDeliveryWindowsFilter,
    cleanFilter: cleanDeliveryWindowsFilter,
    ...deliveryWindowsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findDeliveryWindowsFilterOptions,
    queryKeyFn: getFindDeliveryWindowsFilterOptionsKey,
    paramKeyToSearchMore: 'name',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_FILTERS_KEYS.deliveryWindows,
    defaultValues: defaultFilters?.[CATALOG_FILTERS_KEYS.deliveryWindows],
    shouldIgnoreDefaultFilters,
    formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
    defaultQueryParam: {
      catalogId,
      isAdminSeller,
      onlyActiveProducts: true,
      basedOnList: 'catalogProducts',
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    handleResetFilter: handleResetReferenceColorsFilter,
    getSearchParamKeys: getSearchParamKeysOfReferenceColorsFilter,
    cleanFilter: cleanReferenceColorsFilter,
    ...referenceColorsFilter
  } = useMultiDynamicOptionsSelector({
    queryFn: repository.findReferenceColorsFilterOptions,
    queryKeyFn: getFindReferenceColorsFilterOptionsKey,
    paramKeyToSearchMore: 'name',
    paramKeyToSearchSpecifics: 'ids',
    key: CATALOG_FILTERS_KEYS.colorReferences,
    defaultValues: defaultFilters?.[CATALOG_FILTERS_KEYS.colorReferences],
    shouldIgnoreDefaultFilters,
    formatLabel: (rawData) => rawData.name,
    defaultQueryParam: {
      catalogId,
      isAdminSeller,
      onlyActiveProducts: true,
      basedOnList: 'catalogProducts',
    },
    order: {
      name: 'ASC',
    },
  });

  const {
    cleanFilter: cleanAvailabilityFilter,
    getSearchParamKeys: getSearchParamKeysOfAvailabilityFilter,
    ...availabilityFilter
  } = useMultiStaticOptionsSelector({
    key: CATALOG_FILTERS_KEYS.availability,
    defaultValues: defaultFilters?.[CATALOG_FILTERS_KEYS.availability],
    shouldIgnoreDefaultFilters,
    staticOptions: productAvailabilityOptions.map((av) => ({
      ...av,
      label: translate(av.label),
    })),
  });

  const {
    cleanFilter: cleanProductsWithPromotionFilter,
    getSearchParamKeys: getSearchParamKeysOfProductsWithPromotionFilter,
    ...productsWithPromotionFilter
  } = useRadioFilter({
    key: CATALOG_FILTERS_KEYS.productsWithPromotion,
    defaultValue: defaultFilters?.[CATALOG_FILTERS_KEYS.productsWithPromotion] as string,
    shouldIgnoreDefaultFilters,
    options: [
      { value: 'true', label: translate(LangEnum.GLOBAL_YES) },
      { value: 'false', label: translate(LangEnum.GLOBAL_NO) },
    ],
  });

  const {
    cleanFilter: cleanSuggestedProductsFilter,
    getSearchParamKeys: getSearchParamKeysOfSuggestedProductsFilter,
    ...suggestedProductsFilter
  } = useRadioFilter({
    key: CATALOG_FILTERS_KEYS.onlySuggestedProducts,
    defaultValue: defaultFilters?.[CATALOG_FILTERS_KEYS.onlySuggestedProducts] as string,
    shouldIgnoreDefaultFilters,
    options: [
      { value: 'true', label: translate(LangEnum.GLOBAL_YES) },
      { value: 'false', label: translate(LangEnum.GLOBAL_NO) },
    ],
  });

  const {
    cleanFilter: cleanOnlyOrderAddedProductsFilter,
    getSearchParamKeys: getSearchParamKeysOfOnlyOrderAddedProductsFilter,
    ...onlyOrderAddedProductsFilter
  } = useRadioFilter({
    key: CATALOG_FILTERS_KEYS.onlyOrderAddedProducts,
    defaultValue: defaultFilters?.[CATALOG_FILTERS_KEYS.onlyOrderAddedProducts] as string,
    shouldIgnoreDefaultFilters,
    options: [
      { value: 'true', label: translate(LangEnum.GLOBAL_YES) },
      { value: 'false', label: translate(LangEnum.GLOBAL_NO) },
    ],
  });

  useEffect(() => {
    if (isFirstRender) {
      return;
    }

    handleResetSizesFilter();
    handleResetCategoryFilter();
    handleResetCollectionsFilter();
    handleResetProductColorsFilter();
    handleResetDeliveryWindowFilter();
    handleResetReferenceColorsFilter();
    resetFiltersCreatedByCreator();
    cleanAvailabilityFilter();
    searchTextFilter.cleanFilter();
    cleanProductsWithPromotionFilter();
  }, [isAdminSeller]);

  const options: FilterOptionV3[] = [
    {
      type: PossibleFilters.RADIO,
      title: translate(LangEnum.GLOBAL_SUGGESTED_PRODUCT),
      radioArgs: suggestedProductsFilter,
      cleanFilter: cleanSuggestedProductsFilter,
      getSearchParamKeys: getSearchParamKeysOfSuggestedProductsFilter,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
      toHide: isOfflineMode,
    },
    {
      type: PossibleFilters.RADIO,
      title: translate(LangEnum.GLOBAL_ADDED_PRODUCT),
      radioArgs: onlyOrderAddedProductsFilter,
      cleanFilter: cleanOnlyOrderAddedProductsFilter,
      getSearchParamKeys: getSearchParamKeysOfOnlyOrderAddedProductsFilter,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.CATALOGS_FILTER_REFERENCE_COLOR),
      label: translate(LangEnum.ORDER_FILTER_PRODUCT_COLORS_LABEL),
      multiDynamicOptionsArgs: referenceColorsFilter,
      cleanFilter: cleanReferenceColorsFilter,
      getSearchParamKeys: getSearchParamKeysOfReferenceColorsFilter,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.CATALOGS_FILTER_ORIGINAL_COLOR),
      label: translate(LangEnum.ORDER_FILTER_PRODUCT_COLORS_LABEL),
      multiDynamicOptionsArgs: productColorsFilter,
      cleanFilter: cleanProductColorsFilter,
      getSearchParamKeys: getSearchParamKeysOfProductColorsFilter,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.GLOBAL_SIZE),
      label: translate(LangEnum.GLOBAL_NAME),
      multiDynamicOptionsArgs: productSizesFilter,
      cleanFilter: cleanProductSizesFilter,
      getSearchParamKeys: getSearchParamKeysOfProductSizesFilter,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.CATALOGS_FILTER_CATEGORY),
      label: translate(LangEnum.ORDER_FILTER_CATEGORY_LABEL),
      multiDynamicOptionsArgs: productCategoryFilter,
      cleanFilter: cleanProductCategoryFilter,
      getSearchParamKeys: getSearchParamKeysOfProductCategoryFilter,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.GLOBAL_PRODUCT_COLLECTION),
      label: translate(LangEnum.PRODUCT_FILTER_COLLECTION_NAME_OR_CODE),
      multiDynamicOptionsArgs: collectionsFilter,
      cleanFilter: cleanCollectionsFilter,
      getSearchParamKeys: getSearchParamKeysOfCollectionsFilter,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
    },
    {
      type: PossibleFilters.RADIO,
      title: translate(LangEnum.CATALOGS_FILTER_SALE),
      radioArgs: productsWithPromotionFilter,
      cleanFilter: cleanProductsWithPromotionFilter,
      getSearchParamKeys: getSearchParamKeysOfProductsWithPromotionFilter,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_STATIC_DATA,
      title: translate(LangEnum.GLOBAL_AVAILABILITY),
      hideSearchField: true,
      multiStaticOptionsArgs: availabilityFilter,
      cleanFilter: cleanAvailabilityFilter,
      getSearchParamKeys: getSearchParamKeysOfAvailabilityFilter,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_WITH_DYNAMIC_DATA,
      title: translate(LangEnum.ORDER_FILTER_PRODUCT_DELIVERY_WINDOWS),
      label: translate(LangEnum.CATALOGS_FILTER_DELIVERY_WINDOW_LABEL),
      multiDynamicOptionsArgs: deliveryWindowsFilter,
      cleanFilter: cleanDeliveryWindowsFilter,
      getSearchParamKeys: getSearchParamKeysOfDeliveryWindowsFilter,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
    },
    {
      type: PossibleFilters.NUMBER_RANGE_V2,
      title: translate(LangEnum.CATALOGS_FILTER_SELL_IN_PRICE),
      numberRangeArgs: sellInPriceRangeFilter,
      cleanFilter: sellInPriceRangeFilter.cleanFilter,
      getSearchParamKeys: sellInPriceRangeFilter.getSearchParamKeys,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
      toHide: !showSellInPriceFilter || !canReadSellInPriceFilter || isOfflineMode,
    },
    {
      type: PossibleFilters.OPTIONS_SELECTOR_CREATOR,
      groupBy: LangEnum.GLOBAL_PRODUCTS,
      getLabel: (values) => translate(LangEnum.CATALOGS_FILTER_CLASSIFICATION_TITLE, values),
      getSearchParamKeys: () => {
        const valuesKey = Object.keys(searchParams);
        const keys = valuesKey.filter((key) => key.startsWith(CLASSIFICATIONS_PREFIX));
        return keys;
      },
      cleanFilter: () => {
        clearFiltersCreatedByCreator();
      },
      title: translate(LangEnum.GLOBAL_CLASSIFICATIONS),
      multiDynamicOptionsCreatorArgs: {
        filtersQueryFn: classificationTypeRepository.findClassificationTypes,
        filtersQueryKey: getFindClassificationTypesKey,
        filtersQueryParams: {
          limit: 100,
          skip: 0,
          useAsFilter: true,
          entity: ClassificationTypeEntityEnum.PRODUCT_COLOR,
          'order[exhibition_order]': 'ASC',
        },
        dynamicOptionQueryFn: repository.findClassificationsFilterOptions,
        dynamicOptionQueryKey: getFindClassificationsFilterOptionsKey,
        keys: {
          filterId: 'id',
          filterTitle: 'name',
          idsPrefix: CLASSIFICATIONS_PREFIX,
          keyToRequestOptions: 'classificationTypeId',
        },
        hookArgs: {
          defaultValues: defaultFiltersByPrefix[CLASSIFICATIONS_PREFIX],
          shouldIgnoreDefaultFilters,
          paramKeyToSearchMore: 'query',
          paramKeyToSearchSpecifics: 'ids',
          formatLabel: (rawData) => `${rawData.name}\n${rawData.code}`,
          defaultQueryParam: {
            catalogId,
            isAdminSeller,
            onlyActiveProducts: true,
            basedOnList: 'catalogProducts',
          },
          order: {
            name: 'ASC',
          },
        },
      },
    } as MultiDynamicOptionsCreatorFilter<
      Parameters<typeof classificationTypeRepository.findClassificationTypes>[0],
      Awaited<ReturnType<typeof classificationTypeRepository.findClassificationTypes>>,
      Parameters<typeof repository.findClassificationsFilterOptions>[0],
      Awaited<ReturnType<typeof repository.findClassificationsFilterOptions>>
    > & { groupBy: LangEnum },
  ];

  const searchField = (
    <SearchField
      value={searchTextFilter.value}
      placeholder={LangEnum.CATALOGS_FILTER_PRODUCT_NAME_OR_CODE}
      testId={CatalogProductsTestIds.FILTER_SEARCH}
      inputRef={searchTextFilter.textFieldRef}
      onChange={searchTextFilter.setValue}
      onClear={searchTextFilter.cleanFilter}
    />
  );

  return {
    options,
    preferenceList: filterPreferenceList,
    searchField,
    isSearchActive: Boolean(searchTextFilter.value),
    onClearSearch: searchTextFilter.cleanFilter,
  };
};

export default useCatalogProductsFilters;
